import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '@services/api';
import { useTranslationStore } from '@stores/translationStore';
import { useToast } from '@stores/toastStore';
import { useSessionStore } from '@stores/sessionStore';
import { promptVerifyToOrder } from '@utils/verifyToOrder';
import { useSignalR } from '@hooks/useSignalR';
import { playSound } from '../services/soundService';
import { resolveDisplayValue } from '../i18n';
import PageSpinner from '@components/PageSpinner';
import PageHeader from '@components/PageHeader';
import { emitActivityChanged } from '@utils/activityEvents';
import './LaundryPage.scss';

const getHotelSlug = (paramSlug: string | undefined): string | null => {
  if (paramSlug && paramSlug !== 'undefined') return paramSlug;
  return localStorage.getItem('gcxp_hotel_slug');
};

interface LaundryItem {
  id: number;
  name: string;
  nameTranslationKey?: string;
  description?: string;
  descriptionTranslationKey?: string;
  icon: string;
  price: number;
  priceUSD?: number;
  priceEUR?: number;
  maxQuantity: number;
}

interface LaundryCategory {
  id: number;
  name: string;
  nameTranslationKey?: string;
  icon: string;
  items: LaundryItem[];
}

interface MenuResponse {
  isEnabled: boolean;
  welcomeMessage: string;
  estimatedProcessingHours: number;
  maxRequestsPerDay: number;
  requestsToday: number;
  canRequestMore: boolean;
  guestName: string;
  roomNumber: string;
  currencyCode: string;
  currencySymbol: string;
  categories: LaundryCategory[];
  expressEnabled: boolean;
  expressCutoffTime: string | null;
  expressPricingType: string;
  expressExtraCharge: number;
  guestType?: string;
  preferredCurrency?: string;
  usdExchangeRate?: number;
  eurExchangeRate?: number;
}

interface OrderItem {
  itemId: number;
  itemName: string;
  itemIcon: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

interface LaundryOrder {
  id: string;
  status: string;
  statusCode: number;
  statusChangedAt?: string;
  notes?: string;
  totalPrice: number;
  currencyCode: string;
  currencySymbol: string;
  estimatedReadyTime?: string;
  sentAt?: string;
  receivedAt?: string;
  deliveredAt?: string;
  createdAt: string;
  thankYouMessage?: string;
  isExpress: boolean;
  expressSurcharge: number;
  orderNumber?: string;
  items: OrderItem[];
  progress: { step: number; label: string; labelKey?: string; isComplete: boolean; isCurrent: boolean }[];
}

interface CartItem {
  itemId: number;
  name: string;
  icon: string;
  quantity: number;
  maxQuantity: number;
  unitPrice: number;
}

type ViewState = 'selection' | 'cart' | 'placing' | 'tracking' | 'history';

export default function LaundryPage() {
  const { hotelSlug: paramSlug } = useParams();
  const hotelSlug = getHotelSlug(paramSlug);
  const { loadTranslations, t, language } = useTranslationStore();
  const { toast } = useToast();
  const { isDeviceTrusted } = useSessionStore();

  const [view, setView] = useState<ViewState>('selection');
  const [menu, setMenu] = useState<MenuResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [notes, setNotes] = useState('');
  const [isExpress, setIsExpress] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<LaundryOrder | null>(null);
  const [orderHistory, setOrderHistory] = useState<LaundryOrder[]>([]);
  const { on } = useSignalR();
  const currentOrderIdRef = useRef<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Editing/cancelling a pending order
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  // Listen for real-time laundry status updates via SignalR
  useEffect(() => {
    const unsub = on('LaundryStatusChanged', (data) => {
      const { orderId, status } = data as { orderId: string; status: string };
      if (orderId && currentOrderIdRef.current === orderId) {
        if (status === 'Delivered' || status === 'Received') {
          playSound('request-ready');
        } else {
          playSound('notification');
        }
        apiClient.get<LaundryOrder>(`/api/v1/${hotelSlug}/laundry/order/${orderId}`)
          .then(res => setCurrentOrder(res.data))
          .catch(err => console.error('Failed to refresh laundry order:', err));
      }
    });
    return unsub;
  }, [on, hotelSlug]);

  useEffect(() => {
    const controller = new AbortController();
    if (hotelSlug) {
      fetchMenu(controller.signal);
      fetchHistory(controller.signal);
    }
    if (hotelSlug && language) {
      loadTranslations(hotelSlug, 'laundry', language);
    }
    return () => controller.abort();
  }, [hotelSlug, language]);

  const fetchMenu = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const response = await apiClient.get<MenuResponse>(`/api/v1/${hotelSlug}/laundry/menu`, { signal });
      setMenu(response.data);

      if (response.data.categories.length > 0) {
        setExpandedCategory(response.data.categories[0].id);
      }
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      console.error('Failed to fetch laundry menu:', err);
      setError('Unable to load laundry services');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (signal?: AbortSignal) => {
    try {
      const response = await apiClient.get<LaundryOrder[]>(`/api/v1/${hotelSlug}/laundry/orders`, { signal });
      setOrderHistory(response.data);

      // Auto-track the most recent active order
      const activeOrder = response.data.find(o =>
        !['Delivered', 'Cancelled'].includes(o.status)
      );
      if (activeOrder) {
        setCurrentOrder(activeOrder);
        currentOrderIdRef.current = activeOrder.id;
      }
    } catch (err) {
      console.error('Failed to fetch order history:', err);
    }
  };

  const addToCart = (item: LaundryItem) => {
    setCart(prev => {
      const existing = prev.find(c => c.itemId === item.id);
      if (existing) {
        if (existing.quantity < item.maxQuantity) {
          return prev.map(c =>
            c.itemId === item.id
              ? { ...c, quantity: c.quantity + 1 }
              : c
          );
        }
        return prev;
      }
      return [...prev, {
        itemId: item.id,
        name: item.name,
        icon: item.icon,
        quantity: 1,
        maxQuantity: item.maxQuantity,
        unitPrice: resolveItemPrice(item)
      }];
    });
  };

  const updateCartQuantity = (itemId: number, delta: number) => {
    setCart(prev => {
      return prev
        .map(item => {
          if (item.itemId === itemId) {
            const newQty = item.quantity + delta;
            if (newQty <= 0) return null;
            if (newQty > item.maxQuantity) return item;
            return { ...item, quantity: newQty };
          }
          return item;
        })
        .filter(Boolean) as CartItem[];
    });
  };

  const removeFromCart = (itemId: number) => {
    setCart(prev => prev.filter(item => item.itemId !== itemId));
  };

  const handleSubmitOrder = async () => {
    if (cart.length === 0) return;

    // Chargeable laundry (priced order posts to folio) on a browse-only device → verify first, then retry.
    // A zero-total order isn't a charge, so it's not gated.
    if (getGrandTotal() > 0 && !isDeviceTrusted()) {
      promptVerifyToOrder(() => handleSubmitOrder());
      return;
    }

    try {
      setSubmitting(true);
      setView('placing');

      const body = {
        items: cart.map(item => ({
          itemId: item.itemId,
          quantity: item.quantity
        })),
        notes: notes || undefined,
        isExpress: isExpress
      };

      // Editing an existing pending order → PUT; otherwise place a new one → POST
      const response = editingOrderId
        ? await apiClient.put<LaundryOrder>(`/api/v1/${hotelSlug}/laundry/order/${editingOrderId}`, body)
        : await apiClient.post<LaundryOrder>(`/api/v1/${hotelSlug}/laundry/order`, body);

      setEditingOrderId(null);
      setCurrentOrder(response.data);
      setCart([]);
      setNotes('');
      setIsExpress(false);
      setView('tracking');
      currentOrderIdRef.current = response.data.id;
      playSound('request-sent');
      emitActivityChanged();
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { error?: string | { message?: string }; Error?: string; code?: string } } };
      // Paid laundry needs a verified device: verify, then retry.
      if (e.response?.data?.code === 'verify') {
        setView('cart');
        promptVerifyToOrder(() => handleSubmitOrder());
        return;
      }
      const serverError = typeof e.response?.data?.error === 'string' ? e.response.data.error : e.response?.data?.error?.message;
      // 401 → session expired/checked out; the app-level handler re-validates and routes the guest
      // to sign-in, so just note it briefly rather than implying a retry will help.
      // No response object at all → the request never reached the server (network / connectivity).
      const errorMessage = e.response?.status === 401
        ? (t('common.sessionExpired') || 'Your session expired. Please sign in again.')
        : (serverError
          || e.response?.data?.Error
          || (!e.response
            ? (t('common.networkError') || 'Connection problem — check your internet and try again.')
            : 'Failed to submit request. Please try again.'));
      toast('error', errorMessage);
      setView('cart');
    } finally {
      setSubmitting(false);
    }
  };

  // Re-open a pending order in the cart with its items pre-loaded, to edit it.
  const handleEditOrder = () => {
    if (!currentOrder || !menu) return;
    const allItems = menu.categories.flatMap(c => c.items);
    const rebuilt: CartItem[] = currentOrder.items.map(oi => {
      const mi = allItems.find(i => i.id === oi.itemId);
      return {
        itemId: oi.itemId,
        name: oi.itemName,
        icon: oi.itemIcon,
        quantity: oi.quantity,
        maxQuantity: mi?.maxQuantity ?? oi.quantity,
        unitPrice: oi.unitPrice
      };
    });
    setCart(rebuilt);
    setNotes(currentOrder.notes || '');
    setIsExpress(currentOrder.isExpress);
    setEditingOrderId(currentOrder.id);
    setCancelConfirm(false);
    setError(null);
    // Open the full menu (not just the bag) so the guest can ADD new items too, not only
    // change quantities of what's already in the order. The bag is preserved in state.
    setView('selection');
  };

  const handleCancelOrder = async () => {
    if (!currentOrder) return;
    try {
      setActionBusy(true);
      await apiClient.delete(`/api/v1/${hotelSlug}/laundry/order/${currentOrder.id}`);
      setCancelConfirm(false);
      setCurrentOrder(null);
      currentOrderIdRef.current = null;
      setCart([]);
      setNotes('');
      setIsExpress(false);
      setEditingOrderId(null);
      setView('selection');
      emitActivityChanged();
      await Promise.all([fetchMenu(), fetchHistory()]);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { Error?: string; error?: string } } };
      toast('error', e.response?.data?.Error || e.response?.data?.error || t('laundry.cancelFailed') || 'Failed to cancel order');
      setCancelConfirm(false);
    } finally {
      setActionBusy(false);
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const isForeigner = menu?.guestType === 'foreigner';

  const resolveItemPrice = (item: LaundryItem): number => {
    if (!isForeigner) return item.price;
    if (item.priceUSD != null && item.priceUSD > 0) return item.priceUSD;
    if (menu?.usdExchangeRate && menu.usdExchangeRate > 0) {
      return Math.round((item.price / menu.usdExchangeRate) * 100) / 100;
    }
    return item.price;
  };

  const formatPrice = (price: number) => {
    const symbol = isForeigner ? '$' : (menu?.currencySymbol || '₪');
    return `${symbol}${price.toFixed(2)}`;
  };

  const getTotalItems = () => cart.reduce((sum, item) => sum + item.quantity, 0);
  const getTotalPrice = () => cart.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);

  const getExpressSurcharge = (): number => {
    if (!isExpress || !menu?.expressEnabled) return 0;
    const base = getTotalPrice();
    if (menu.expressPricingType === 'percentage') {
      return base * (menu.expressExtraCharge / 100);
    }
    return menu.expressExtraCharge;
  };

  const getGrandTotal = () => getTotalPrice() + getExpressSurcharge();

  const isExpressAvailable = (): boolean => {
    if (!menu?.expressEnabled || !menu?.expressCutoffTime) return false;
    const now = new Date();
    const [h, m] = menu.expressCutoffTime.split(':').map(Number);
    const cutoff = new Date();
    cutoff.setHours(h, m, 0, 0);
    return now < cutoff;
  };

  // Loading state
  if (loading) {
    return <PageSpinner />;
  }

  // Error state
  if (error && !menu) {
    return (
      <div className="laundry-page error">
        <div className="error-content">
          <span className="error-icon">⚠️</span>
          <h2>{t('common.oops') || 'Oops!'}</h2>
          <p>{error}</p>
          <button onClick={() => fetchMenu()} className="retry-btn">{t('common.tryAgain') || 'Try Again'}</button>
        </div>
      </div>
    );
  }

  // Not enabled
  if (menu && !menu.isEnabled) {
    return (
      <div className="laundry-page closed">
        <div className="closed-content">
          <span className="icon">🌙</span>
          <h2>{t('laundry.unavailable') || 'Laundry Service Unavailable'}</h2>
          <p>{t('laundry.unavailableMsg') || 'Laundry service is not available at this time.'}</p>
          <p>{t('laundry.contactDesk') || 'Please contact the front desk for assistance.'}</p>
        </div>
      </div>
    );
  }

  // Daily limit reached
  if (menu && !menu.canRequestMore) {
    return (
      <div className="laundry-page limit">
        <div className="limit-content">
          <span className="icon">✅</span>
          <h2>{t('laundry.limitReached') || 'Daily Limit Reached'}</h2>
          <p>{t('laundry.tryTomorrow') || 'You have reached your daily laundry request limit. Please try again tomorrow.'}</p>
        </div>
      </div>
    );
  }

  // Placing order state
  if (view === 'placing') {
    return (
      <div className="laundry-page placing">
        <div className="placing-content">
          <div className="icon-spin">👔</div>
          <h2>{t('laundry.submitting') || 'Submitting...'}</h2>
          <p>{t('laundry.sendingMsg') || 'Sending your laundry request...'}</p>
        </div>
      </div>
    );
  }

  // Tracking view
  if (view === 'tracking' && currentOrder) {
    return (
      <div className="laundry-page tracking">
        <PageHeader
          title={<>👔 {t('laundry.yourOrder') || 'Your Laundry'}{currentOrder.orderNumber ? ` #${currentOrder.orderNumber}` : ''}</>}
          leading={
            <button className="back-btn" onClick={() => { setView('selection'); fetchMenu(); }}>
              ← {t('common.back') || 'Back'}
            </button>
          }
        />

        <div className="order-card">
          <div className="order-summary">
            <div className="items-list">
              {currentOrder.items.map((item, idx) => (
                <span key={idx} className="item-badge">
                  {item.itemIcon} {item.quantity}× {item.itemName}
                </span>
              ))}
            </div>
            <div className="order-meta">
              <p className="order-time">{t('laundry.submitted') || 'Submitted'} {formatTime(currentOrder.createdAt)}</p>
              <p className="order-total">{t('laundry.total') || 'Total'}: {currentOrder.currencySymbol}{currentOrder.totalPrice.toFixed(2)}</p>
            </div>
          </div>

          <div className="progress-tracker">
            {currentOrder.progress.map((step, index) => (
              <div
                key={step.step}
                className={`progress-step ${step.isComplete ? 'complete' : ''} ${step.isCurrent ? 'current' : ''}`}
              >
                <div className="step-indicator">
                  {step.isComplete ? '✓' : step.step}
                </div>
                <div className="step-content">
                  <span className="step-label">
                    {step.labelKey ? (t(step.labelKey) !== step.labelKey ? t(step.labelKey) : step.label) : step.label}
                  </span>
                </div>
                {index < currentOrder.progress.length - 1 && (
                  <div className={`step-connector ${step.isComplete ? 'complete' : ''}`} />
                )}
              </div>
            ))}
          </div>

          {currentOrder.isExpress && (
            <div className="status-alert express">
              <span>⚡</span>
              <p>{t('laundry.expressMsg') || 'Express same-day delivery'}</p>
            </div>
          )}

          {currentOrder.status === 'Acknowledged' && (
            <div className="status-alert acknowledged">
              <span>✅</span>
              <p>{t('laundry.acknowledgedMsg') || 'Your laundry request has been acknowledged'}</p>
            </div>
          )}

          {currentOrder.status === 'Sent' && (
            <div className="status-alert sent">
              <span>🚚</span>
              <p>{t('laundry.sentMsg') || 'Your laundry has been sent for cleaning'}</p>
            </div>
          )}

          {currentOrder.status === 'Received' && (
            <div className="status-alert received">
              <span>📦</span>
              <p>{t('laundry.receivedMsg') || 'Your laundry has been received back and is ready for delivery!'}</p>
            </div>
          )}

          {currentOrder.status === 'Delivered' && (
            <div className="status-alert delivered">
              <span>🎉</span>
              <p>{t('laundry.deliveredMsg') || 'Your laundry has been delivered!'}</p>
            </div>
          )}

          {currentOrder.estimatedReadyTime && !['Delivered'].includes(currentOrder.status) && (
            <div className="eta-info">
              <span>⏱️</span>
              <p>{t('laundry.estimatedReady') || 'Estimated ready'}: {formatDate(currentOrder.estimatedReadyTime)}</p>
            </div>
          )}

          {currentOrder.status === 'Pending' && (
            <div className="pending-actions" style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                onClick={handleEditOrder}
                disabled={actionBusy}
                style={{ flex: 1, padding: '10px 12px', background: '#fff', color: '#1E40AF', border: '1px solid #3B82F6', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
              >
                ✏️ {t('laundry.editOrder') || 'Edit Order'}
              </button>
              {!cancelConfirm ? (
                <button
                  onClick={() => setCancelConfirm(true)}
                  disabled={actionBusy}
                  style={{ flex: 1, padding: '10px 12px', background: '#fff', color: '#B91C1C', border: '1px solid #EF4444', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
                >
                  🗑️ {t('laundry.cancelOrder') || 'Cancel Order'}
                </button>
              ) : (
                <button
                  onClick={handleCancelOrder}
                  disabled={actionBusy}
                  style={{ flex: 1, padding: '10px 12px', background: '#EF4444', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
                >
                  {actionBusy ? '…' : (t('laundry.confirmCancel') || 'Tap to confirm')}
                </button>
              )}
            </div>
          )}
        </div>

        <button
          className="new-order-btn"
          onClick={() => {
            setCurrentOrder(null);
            setView('selection');
            fetchMenu();
          }}
        >
          {t('laundry.newRequest') || 'New Laundry Request'}
        </button>
      </div>
    );
  }

  // Order History view
  if (view === 'history') {
    return (
      <div className="laundry-page history-view">
        <PageHeader
          title={<>📋 {t('laundry.history') || 'Order History'}</>}
          leading={
            <button className="back-btn" onClick={() => setView('selection')}>
              ← {t('common.back') || 'Back'}
            </button>
          }
        />

        {orderHistory.length === 0 ? (
          <div className="empty-history">
            <span>👔</span>
            <p>{t('laundry.noOrders') || 'No laundry orders yet'}</p>
          </div>
        ) : (
          <div className="history-list">
            {orderHistory.map(order => (
              <button
                key={order.id}
                className="history-item w-100 text-start border-0"
                onClick={() => {
                  setCurrentOrder(order);
                  currentOrderIdRef.current = order.id;
                  setView('tracking');
                }}
              >
                <div className="history-item-header">
                  <span className={`status-badge ${order.status.toLowerCase()}`}>
                    {order.status}
                  </span>
                  {order.orderNumber && <span className="history-order-number">#{order.orderNumber}</span>}
                  <span className="history-date">{formatDate(order.createdAt)}</span>
                </div>
                <div className="history-item-details">
                  <div className="history-items">
                    {order.items.slice(0, 3).map((item, idx) => (
                      <span key={idx}>{item.itemIcon} {item.quantity}× {item.itemName}</span>
                    ))}
                    {order.items.length > 3 && <span>+{order.items.length - 3} more</span>}
                  </div>
                  <span className="history-total">{order.currencySymbol}{order.totalPrice.toFixed(2)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Cart view
  if (view === 'cart') {
    return (
      <div className="laundry-page cart-view">
        <PageHeader
          title={<>🛒 {t('laundry.yourBag') || 'Your Laundry Bag'}</>}
          leading={
            <button className="back-btn" onClick={() => setView('selection')}>
              ← {t('common.back') || 'Back'}
            </button>
          }
        />

        {error && (
          <div className="inline-error">
            <span>⚠️</span> {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {editingOrderId && (
          <div className="editing-banner" style={{ background: '#EFF6FF', border: '1px solid #3B82F6', borderRadius: 8, padding: '10px 14px', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>✏️</span>
            <div style={{ flex: 1 }}>
              <strong style={{ color: '#1E40AF', fontSize: 14 }}>{t('laundry.editingOrder') || 'Editing your order'}</strong>
              <p style={{ color: '#1D4ED8', fontSize: 12, margin: 0 }}>{t('laundry.editingNote') || 'Adjust the items, then tap Update.'}</p>
            </div>
            <button
              onClick={() => { setEditingOrderId(null); setCart([]); setNotes(''); setIsExpress(false); setView('tracking'); }}
              style={{ background: 'transparent', border: 'none', color: '#1E40AF', cursor: 'pointer', fontWeight: 700, fontSize: 16 }}
              aria-label={t('common.cancel') || 'Cancel'}
            >×</button>
          </div>
        )}

        <div className="cart-items">
          {cart.map(item => (
            <div key={item.itemId} className="cart-item">
              <span className="item-icon">{item.icon}</span>
              <div className="item-info">
                <span className="item-name">{item.name}</span>
                <span className="item-price">{formatPrice(item.unitPrice)} {t('laundry.each') || 'each'}</span>
              </div>
              <div className="quantity-controls">
                <button onClick={() => updateCartQuantity(item.itemId, -1)}>−</button>
                <span>{item.quantity}</span>
                <button
                  onClick={() => updateCartQuantity(item.itemId, 1)}
                  disabled={item.quantity >= item.maxQuantity}
                >+</button>
              </div>
              <span className="item-subtotal">{formatPrice(item.unitPrice * item.quantity)}</span>
              <button className="remove-btn" onClick={() => removeFromCart(item.itemId)}>
                🗑️
              </button>
            </div>
          ))}
        </div>

        <div className="notes-section">
          <label>{t('laundry.specialInstructions') || 'Special Instructions'}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('laundry.notesPlaceholder') || 'Any special care instructions, stain notes, etc.'}
            maxLength={1000}
          />
        </div>

        <div className="cart-footer">
          {isExpressAvailable() && (
            <div className="express-option">
              <label className="express-toggle">
                <input
                  type="checkbox"
                  checked={isExpress}
                  onChange={() => setIsExpress(!isExpress)}
                />
                <span className="express-label">
                  ⚡ {t('laundry.expressDelivery') || 'Express Same-Day Delivery'}
                </span>
              </label>
              {isExpress && (
                <div className="express-detail">
                  <span className="express-surcharge">
                    +{formatPrice(getExpressSurcharge())}
                    {menu?.expressPricingType === 'percentage' && ` (${menu.expressExtraCharge}%)`}
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="price-summary">
            <div className="price-row subtotal">
              <span>{t('laundry.subtotal') || 'Subtotal'}</span>
              <span>{formatPrice(getTotalPrice())}</span>
            </div>
            {isExpress && getExpressSurcharge() > 0 && (
              <div className="price-row express-fee">
                <span>⚡ {t('laundry.expressFee') || 'Express Fee'}</span>
                <span>+{formatPrice(getExpressSurcharge())}</span>
              </div>
            )}
            <div className="price-row total">
              <span>{t('laundry.total') || 'Total'}</span>
              <span>{formatPrice(getGrandTotal())}</span>
            </div>
          </div>
          <div className="delivery-info">
            <span>⏱️</span>
            <p>{t('laundry.estimatedProcessing') || 'Estimated processing'}: ~{menu?.estimatedProcessingHours || 24} {t('laundry.hours') || 'hours'}</p>
          </div>
          <button
            className="submit-btn"
            onClick={handleSubmitOrder}
            disabled={cart.length === 0 || submitting}
          >
            {(getGrandTotal() > 0 && !isDeviceTrusted())
              ? `🔒 ${t('verify.verifyToCharge') || 'Verify to charge to room'}`
              : `${(editingOrderId ? (t('laundry.updateOrder') || 'Update Order') : (t('laundry.submitOrder') || 'Send Laundry Bag'))} (${getTotalItems()} ${getTotalItems() !== 1 ? (t('laundry.items') || 'items') : (t('laundry.item') || 'item')})`}
          </button>
        </div>
      </div>
    );
  }

  // Selection view (default)
  return (
    <div className="laundry-page selection page-content">
      <PageHeader
        title={<>👔 {t('laundry.title') || 'Laundry Service'}</>}
        subtitle={
          menu?.welcomeMessage ||
          (menu?.roomNumber ? `${t('laundry.room') || 'Room'} ${menu.roomNumber}` : undefined)
        }
      />

      {error && (
        <div className="inline-error">
          <span>⚠️</span> {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Editing banner — guest is editing a pending order (add or remove items, then update). */}
      {editingOrderId && (
        <div className="editing-banner" style={{ background: '#EFF6FF', border: '1px solid #3B82F6', borderRadius: 8, padding: '10px 14px', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 20 }}>✏️</span>
          <div style={{ flex: 1 }}>
            <strong style={{ color: '#1E40AF', fontSize: 14 }}>{t('laundry.editingOrder') || 'Editing your order'}</strong>
            <p style={{ color: '#1D4ED8', fontSize: 12, margin: 0 }}>{t('laundry.editingAddNote') || 'Add new items below or adjust your bag, then tap View Bag to update.'}</p>
          </div>
          <button
            onClick={() => { setEditingOrderId(null); setCart([]); setNotes(''); setIsExpress(false); setView('tracking'); }}
            style={{ background: 'transparent', border: 'none', color: '#1E40AF', cursor: 'pointer', fontWeight: 700, fontSize: 16 }}
            aria-label={t('common.cancel') || 'Cancel'}
          >×</button>
        </div>
      )}

      {/* Active orders banner (hidden while editing) */}
      {!editingOrderId && currentOrder && !['Delivered', 'Cancelled'].includes(currentOrder.status) && (
        <button
          className="active-order-banner w-100 text-start border-0"
          onClick={() => setView('tracking')}
        >
          <span>👔</span>
          <div className="banner-info">
            <strong>{t('laundry.activeOrder') || 'Active Laundry Order'}</strong>
            <span className="banner-status">{currentOrder.status}</span>
          </div>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      )}

      {/* Order history link */}
      {orderHistory.length > 0 && (
        <button className="history-link" onClick={() => setView('history')}>
          📋 {t('laundry.viewHistory') || 'View Order History'} ({orderHistory.length})
        </button>
      )}

      <div className="categories">
        {menu?.categories.map(category => (
          <div key={category.id} className="category">
            <button
              className={`category-header ${expandedCategory === category.id ? 'expanded' : ''}`}
              onClick={() => setExpandedCategory(
                expandedCategory === category.id ? null : category.id
              )}
            >
              <span className="category-icon">{category.icon}</span>
              <span className="category-name">{resolveDisplayValue(category.nameTranslationKey || category.name, t)}</span>
              <span className="expand-icon">{expandedCategory === category.id ? '▼' : '▶'}</span>
            </button>

            {expandedCategory === category.id && (
              <div className="category-items">
                {category.items.map(item => {
                  const inCart = cart.find(c => c.itemId === item.id);
                  return (
                    <div key={item.id} className="item-card">
                      <span className="item-icon">{item.icon}</span>
                      <div className="item-info">
                        <span className="item-name">{resolveDisplayValue(item.nameTranslationKey || item.name, t)}</span>
                        {item.description && (
                          <span className="item-desc">{resolveDisplayValue(item.descriptionTranslationKey || item.description, t)}</span>
                        )}
                        <span className="item-price">{formatPrice(resolveItemPrice(item))}</span>
                      </div>
                      {inCart ? (
                        <div className="quantity-controls">
                          <button onClick={() => updateCartQuantity(item.id, -1)}>−</button>
                          <span>{inCart.quantity}</span>
                          <button
                            onClick={() => updateCartQuantity(item.id, 1)}
                            disabled={inCart.quantity >= item.maxQuantity}
                          >+</button>
                        </div>
                      ) : (
                        <button
                          className="add-btn"
                          onClick={() => addToCart(item)}
                        >
                          + {t('laundry.add') || 'Add'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {cart.length > 0 && (
        <div className="cart-fab">
          <button onClick={() => setView('cart')}>
            🛒 {t('laundry.viewBag') || 'View Bag'} ({getTotalItems()}) — {formatPrice(getTotalPrice())}
          </button>
        </div>
      )}
    </div>
  );
}
